import React, { useEffect, useRef } from 'react';

/**
 * NetworkBackground — full-viewport canvas mesh that suggests a
 * distributed transport layer (nodes + edges). Mouse-aware: nearby
 * nodes drift toward the cursor and edges brighten near it.
 *
 * Palette: DT teal nodes with occasional amber accents.
 * Pauses when tab is hidden. Honors prefers-reduced-motion.
 */
function NetworkBackground() {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d', { alpha: true });
        if (!ctx) return;

        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const driftScale = reduceMotion ? 0 : 1;

        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        let width = 0;
        let height = 0;

        const resize = () => {
            width = window.innerWidth;
            height = window.innerHeight;
            canvas.width = Math.floor(width * dpr);
            canvas.height = Math.floor(height * dpr);
            canvas.style.width = width + 'px';
            canvas.style.height = height + 'px';
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        };
        resize();

        const isCoarse = window.matchMedia('(pointer: coarse)').matches;
        const lowCore = (navigator.hardwareConcurrency || 4) < 4;
        const baseDensity = isCoarse || lowCore ? 22000 : 30000;
        const targetCount = Math.round((width * height) / baseDensity);
        const NODE_COUNT = Math.max(12, Math.min(isCoarse ? 26 : 48, targetCount));

        const LINK_DIST = isCoarse ? 130 : 160;
        const LINK_DIST_SQ = LINK_DIST * LINK_DIST;
        const MOUSE_RADIUS = 180;
        const MOUSE_RADIUS_SQ = MOUSE_RADIUS * MOUSE_RADIUS;

        const rand = (a, b) => a + Math.random() * (b - a);
        const nodes = new Array(NODE_COUNT).fill(0).map(() => ({
            x: Math.random() * width,
            y: Math.random() * height,
            vx: rand(-0.18, 0.18) * driftScale,
            vy: rand(-0.18, 0.18) * driftScale,
            r: rand(1.6, 3.0),
            // ~15% of nodes are amber to evoke logo's rocket streak
            color: Math.random() < 0.15 ? 'amber' : 'teal',
        }));

        const mouse = { x: -9999, y: -9999, active: false };
        const onMove = (e) => { mouse.x = e.clientX; mouse.y = e.clientY; mouse.active = true; };
        const onLeave = () => { mouse.active = false; mouse.x = -9999; mouse.y = -9999; };
        const onTouch = (e) => {
            if (e.touches && e.touches[0]) {
                mouse.x = e.touches[0].clientX;
                mouse.y = e.touches[0].clientY;
                mouse.active = true;
            }
        };
        window.addEventListener('mousemove', onMove, { passive: true });
        window.addEventListener('mouseleave', onLeave);
        window.addEventListener('touchmove', onTouch, { passive: true });
        window.addEventListener('touchend', onLeave);

        let resizeTimer = null;
        const onResize = () => {
            if (resizeTimer) clearTimeout(resizeTimer);
            resizeTimer = setTimeout(resize, 120);
        };
        window.addEventListener('resize', onResize);

        let running = true;
        const onVisibility = () => {
            running = !document.hidden;
            if (running) loop();
        };
        document.addEventListener('visibilitychange', onVisibility);

        const draw = () => {
            ctx.clearRect(0, 0, width, height);

            for (let i = 0; i < nodes.length; i++) {
                const n = nodes[i];
                n.x += n.vx;
                n.y += n.vy;

                if (mouse.active) {
                    const dx = mouse.x - n.x;
                    const dy = mouse.y - n.y;
                    const d2 = dx * dx + dy * dy;
                    if (d2 < MOUSE_RADIUS_SQ && d2 > 1) {
                        const f = (1 - d2 / MOUSE_RADIUS_SQ) * 0.04;
                        n.vx += (dx / Math.sqrt(d2)) * f;
                        n.vy += (dy / Math.sqrt(d2)) * f;
                    }
                }

                n.vx *= 0.985;
                n.vy *= 0.985;

                if (driftScale && Math.abs(n.vx) < 0.04) n.vx += rand(-0.04, 0.04);
                if (driftScale && Math.abs(n.vy) < 0.04) n.vy += rand(-0.04, 0.04);

                if (n.x < -10) n.x = width + 10;
                else if (n.x > width + 10) n.x = -10;
                if (n.y < -10) n.y = height + 10;
                else if (n.y > height + 10) n.y = -10;
            }

            // edges
            for (let i = 0; i < nodes.length; i++) {
                const a = nodes[i];
                for (let j = i + 1; j < nodes.length; j++) {
                    const b = nodes[j];
                    const dx = a.x - b.x;
                    const dy = a.y - b.y;
                    const d2 = dx * dx + dy * dy;
                    if (d2 > LINK_DIST_SQ) continue;
                    const t = 1 - d2 / LINK_DIST_SQ;
                    let boost = 0;
                    if (mouse.active) {
                        const mx = (a.x + b.x) * 0.5;
                        const my = (a.y + b.y) * 0.5;
                        const mdx = mx - mouse.x;
                        const mdy = my - mouse.y;
                        const md2 = mdx * mdx + mdy * mdy;
                        if (md2 < MOUSE_RADIUS_SQ) {
                            boost = (1 - md2 / MOUSE_RADIUS_SQ) * 0.32;
                        }
                    }
                    const alpha = Math.min(0.28, t * 0.18 + boost);
                    ctx.strokeStyle = `rgba(0, 212, 170, ${alpha})`;
                    ctx.lineWidth = 1;
                    ctx.beginPath();
                    ctx.moveTo(a.x, a.y);
                    ctx.lineTo(b.x, b.y);
                    ctx.stroke();
                }
            }

            // nodes
            for (let i = 0; i < nodes.length; i++) {
                const n = nodes[i];
                let glow = 0;
                if (mouse.active) {
                    const dx = mouse.x - n.x;
                    const dy = mouse.y - n.y;
                    const d2 = dx * dx + dy * dy;
                    if (d2 < MOUSE_RADIUS_SQ) {
                        glow = (1 - d2 / MOUSE_RADIUS_SQ) * 0.5;
                    }
                }
                const alpha = 0.42 + glow;
                ctx.fillStyle = n.color === 'amber'
                    ? `rgba(243, 149, 35, ${alpha})`
                    : `rgba(0, 212, 170, ${alpha})`;
                ctx.beginPath();
                ctx.arc(n.x, n.y, n.r + glow * 1.2, 0, Math.PI * 2);
                ctx.fill();
            }
        };

        let rafId = 0;
        const loop = () => {
            if (!running) return;
            draw();
            rafId = requestAnimationFrame(loop);
        };
        loop();

        return () => {
            running = false;
            cancelAnimationFrame(rafId);
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseleave', onLeave);
            window.removeEventListener('touchmove', onTouch);
            window.removeEventListener('touchend', onLeave);
            window.removeEventListener('resize', onResize);
            document.removeEventListener('visibilitychange', onVisibility);
            if (resizeTimer) clearTimeout(resizeTimer);
        };
    }, []);

    return <canvas ref={canvasRef} aria-hidden="true" className="network-bg" />;
}

export default NetworkBackground;
